import React, { useState, useEffect, useCallback, useMemo } from "react";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import axiosInstance from "../../../axiosInstance";
import "./StockUpdate.scss";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/AuthContext";
import AccessDenied from "../../AccessDenied";
import StockQRModal from "./StockQRModal";
import { RackPicker, WarehousePicker } from "./ChipPickers";
import StockQRScanner from "./StockQRScanner";
import StockScanPopup from "./StockScanPopup";

const UNITS = [
  { value: "MM", label: "Millimeter (MM)" },
  { value: "CM", label: "Centimeter (CM)" },
  { value: "METER", label: "Meter" },
  { value: "KM", label: "Kilometer (KM)" },
  { value: "INCH", label: "Inch" },
  { value: "FOOT", label: "Foot" },
  { value: "YARD", label: "Yard" },
  { value: "MILE", label: "Mile" },
  { value: "SQ_MM", label: "Square Millimeter" },
  { value: "SQ_CM", label: "Square Centimeter" },
  { value: "SQ_METER", label: "Square Meter" },
  { value: "SQ_KM", label: "Square Kilometer" },
  { value: "SQ_INCH", label: "Square Inch" },
  { value: "SQ_FOOT", label: "Square Foot" },
  { value: "SQ_YARD", label: "Square Yard" },
  { value: "SQ_MILE", label: "Square Mile" },
  { value: "ACRE", label: "Acre" },
  { value: "HECTARE", label: "Hectare" },
  { value: "MG", label: "Milligram (MG)" },
  { value: "GRAM", label: "Gram" },
  { value: "KG", label: "Kilogram (KG)" },
  { value: "TON", label: "Ton" },
  { value: "OUNCE", label: "Ounce" },
  { value: "POUND", label: "Pound" },
  { value: "QUINTAL", label: "Quintal" },
  { value: "ML", label: "Milliliter (ML)" },
  { value: "LITER", label: "Liter" },
  { value: "CUBIC_CM", label: "Cubic Centimeter" },
  { value: "CUBIC_METER", label: "Cubic Meter" },
  { value: "CUBIC_INCH", label: "Cubic Inch" },
  { value: "CUBIC_FOOT", label: "Cubic Foot" },
  { value: "GALLON", label: "Gallon" },
  { value: "BARREL", label: "Barrel" },
  { value: "PIECE", label: "Piece" },
  { value: "UNIT", label: "Unit" },
  { value: "PACK", label: "Pack" },
  { value: "BOX", label: "Box" },
  { value: "DOZEN", label: "Dozen" },
  { value: "BUNDLE", label: "Bundle" },
  { value: "SET", label: "Set" },
  { value: "PAIR", label: "Pair" },
  { value: "CARTON", label: "Carton" },
  { value: "CASE", label: "Case" },
  { value: "PALLET", label: "Pallet" },
  { value: "SECOND", label: "Second" },
  { value: "MINUTE", label: "Minute" },
  { value: "HOUR", label: "Hour" },
  { value: "DAY", label: "Day" },
  { value: "WEEK", label: "Week" },
  { value: "MONTH", label: "Month" },
  { value: "YEAR", label: "Year" },
  { value: "BYTE", label: "Byte" },
  { value: "KB", label: "Kilobyte (KB)" },
  { value: "MB", label: "Megabyte (MB)" },
  { value: "GB", label: "Gigabyte (GB)" },
  { value: "TB", label: "Terabyte (TB)" },
  { value: "WATT", label: "Watt" },
  { value: "KILOWATT", label: "Kilowatt" },
  { value: "MEGAWATT", label: "Megawatt" },
  { value: "JOULE", label: "Joule" },
  { value: "CALORIE", label: "Calorie" },
  { value: "KCAL", label: "Kilocalorie (KCAL)" },
  { value: "VOLT", label: "Volt" },
  { value: "AMPERE", label: "Ampere" },
  { value: "PERCENT", label: "Percent (%)" },
  { value: "CURRENCY", label: "Currency" },
  { value: "POINT", label: "Point" },
  { value: "SHEET", label: "Sheet" },
  { value: "ROLL", label: "Roll" },
  { value: "STRIP", label: "Strip" },
  { value: "LENGTH", label: "Length" },
  { value: "WEIGHT", label: "Weight" },
  { value: "VOLUME", label: "Volume" },
  { value: "AREA", label: "Area" },
  { value: "SIZE", label: "Size" },
  { value: "OTHER", label: "Other" },
];

// Variant info used on the printed QR labels
const toQrCombination = (comb) => ({
  id: comb.combinationId,
  name: comb.combinationDisplayName || comb.combinationSku || "Default",
  sku: comb.combinationSku || "",
  racks: [
    ...new Set(
      (comb.warehouseStocks || []).map((st) => st.rackNumber).filter(Boolean),
    ),
  ].join(", "),
});

const StockUpdate = ({ markPageAsCompleted }) => {
  const [activeTab, setActiveTab] = useState("raw-materials");
  const [viewMode, setViewMode] = useState("cards");
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [editableStocks, setEditableStocks] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [qrTarget, setQrTarget] = useState(null);
  const [showScanner, setShowScanner] = useState(false);
  const [scannedParams, setScannedParams] = useState(null);

  const handleScanned = useCallback((params) => {
    setShowScanner(false);
    setScannedParams(params);
  }, []);
  // Stock transaction type for the current edit session: INBOUND | OUTBOUND | TRANSFER
  const [transactionType, setTransactionType] = useState("INBOUND");

  // QR labels for a whole product / raw material (one label per variant)
  const openGroupQR = useCallback(
    (group) =>
      setQrTarget({
        scope: "group",
        type: group.type,
        materialId: group.materialId,
        materialName: group.materialName,
        materialCode: group.materialCode,
        combinations: group.combinations.map(toQrCombination),
      }),
    [],
  );

  // QR label for a single combination (variant)
  const openVariantQR = useCallback(
    (group, comb) =>
      setQrTarget({
        scope: "variant",
        type: group.type,
        materialId: group.materialId,
        materialName: group.materialName,
        materialCode: group.materialCode,
        combinations: [toQrCombination(comb)],
      }),
    [],
  );

  const SIDEBAR_COLLAPSED_KEY = "stock_update_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  const INFO_CARD_COLLAPSED_KEY = "stock_update_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // Derived, real-data metric stats (no fabricated numbers)
  const stockStats = useMemo(() => {
    let totalQuantity = 0;
    let zeroStockItems = 0;
    const warehouseIds = new Set();

    editableStocks.forEach((group) => {
      if ((group.totalMaterialStock || 0) === 0) zeroStockItems += 1;
      totalQuantity += group.totalMaterialStock || 0;
      group.combinations?.forEach((comb) => {
        comb.warehouseStocks?.forEach((stock) => {
          if (stock.warehouseId) warehouseIds.add(stock.warehouseId);
        });
      });
    });

    return {
      totalItems: editableStocks.length,
      totalQuantity,
      warehousesInUse: warehouseIds.size,
      zeroStockItems,
    };
  }, [editableStocks]);

  const generateUniqueStockKey = useCallback(
    (materialId, stockId, warehouseId, combinationId = 0, isNew = false) => {
      if (isNew) return `new-${materialId}-${combinationId}-${Date.now()}`;
      return `${materialId}-${stockId}-${warehouseId}-${combinationId}`;
    },
    [],
  );

  // Only INV-MANAGER can use this page, so it always talks to the inv-manager API
  const getApiBaseUrl = useCallback(
    () => process.env.REACT_APP_API_INV_MANAGER_URL,
    [],
  );

  const getWarehouseEndpoint = useCallback(() => "/warehouses", []);

  const loadMaterials = useCallback(async () => {
    try {
      const endpoint =
        activeTab === "raw-materials"
          ? "/warehouse-stock/raw-materials"
          : "/warehouse-stock/products";

      const response = await axiosInstance.get(`${getApiBaseUrl()}${endpoint}`);
      const items = response.data?.data || [];

      const groupedCards = [];

      items.forEach((item) => {
        const materialId =
          activeTab === "raw-materials" ? item.rawMaterialId : item.productId;
        const materialName =
          activeTab === "raw-materials"
            ? item.rawMaterialName
            : item.productName;
        const materialCode =
          activeTab === "raw-materials"
            ? item.rawMaterialCode
            : item.productCode;

        // Create a parent group for the material
        const materialGroup = {
          materialId,
          materialName,
          materialCode,
          type: activeTab === "raw-materials" ? "raw-material" : "product",
          combinations: [],
          totalMaterialStock: 0,
        };

        item.combinations?.forEach((combination) => {
          const combinationData = {
            combinationId: combination.combinationId || 0,
            combinationDisplayName: combination.combinationDisplayName || null,
            combinationSku: combination.combinationSku || null,
            totalStock: combination.totalStock || 0,
            warehouseStocks: [],
          };

          materialGroup.totalMaterialStock += combination.totalStock || 0;

          if (
            combination.warehouseStocks &&
            combination.warehouseStocks.length > 0
          ) {
            combination.warehouseStocks.forEach((stock) => {
              combinationData.warehouseStocks.push({
                id: stock.id,
                uniqueKey: generateUniqueStockKey(
                  materialId,
                  stock.id,
                  stock.warehouseId,
                  combination.combinationId,
                ),
                warehouseId: stock.warehouseId,
                warehouseName: stock.warehouseName,
                rackNumber: stock.rackNumber || "",
                rawMaterialId:
                  stock.rawMaterialId ||
                  (activeTab === "raw-materials" ? materialId : 0),
                productId:
                  stock.productId ||
                  (activeTab === "products" ? materialId : 0),
                combinationId: combination.combinationId || 0,
                combinationDisplayName:
                  combination.combinationDisplayName || null,
                combinationSku: combination.combinationSku || null,
                isNew: false,
                originalQuantity: stock.quantity || 0,
                quantity: stock.quantity || 0,
                quantityUpdate: "",
                unit: stock.unit || "PIECE",
              });
            });
          }

          materialGroup.combinations.push(combinationData);
        });

        groupedCards.push(materialGroup);
      });

      setEditableStocks(groupedCards);

      if (items.length > 0) markPageAsCompleted("Stock Levels");
    } catch (err) {
      console.error("Load materials error:", err);
    }
  }, [activeTab, getApiBaseUrl, markPageAsCompleted, generateUniqueStockKey]);

  const fetchWarehouses = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${getApiBaseUrl()}${getWarehouseEndpoint()}`,
      );
      setWarehouses(response.data?.data || []);
    } catch (err) {
      console.error("Error fetching warehouses:", err);
    }
  }, [getApiBaseUrl, getWarehouseEndpoint]);

  const canAccessStockUpdate = useCallback(
    () => !!user?.authorities?.includes("ROLE_INVENTORY-MANAGER"),
    [user],
  );

  useEffect(() => {
    if (!canAccessStockUpdate()) return;
    fetchWarehouses();
    loadMaterials();
  }, [activeTab, loadMaterials, fetchWarehouses, canAccessStockUpdate]);

  useEffect(() => {
    setBulkUpdateEnabled(false);
  }, [activeTab]);

  useEffect(() => {
    if (!bulkUpdateEnabled) setTransactionType("INBOUND");
  }, [bulkUpdateEnabled]);

  const handleQuantityUpdate = useCallback(
    (materialId, combinationId, uniqueKey, value) => {
      setEditableStocks((prev) =>
        prev.map((group) =>
          group.materialId === materialId
            ? {
                ...group,
                combinations: group.combinations.map((comb) =>
                  comb.combinationId === combinationId
                    ? {
                        ...comb,
                        warehouseStocks: comb.warehouseStocks.map((stock) =>
                          stock.uniqueKey === uniqueKey
                            ? { ...stock, quantityUpdate: value }
                            : stock,
                        ),
                      }
                    : comb,
                ),
              }
            : group,
        ),
      );
    },
    [],
  );

  useEffect(() => {
    if (bulkUpdateEnabled) {
      // Switch to card view
      setViewMode("cards");

      // Auto-expand all groups
      const allMaterialIds = editableStocks.map((group) => group.materialId);
      setExpandedGroups(new Set(allMaterialIds));
    }
  }, [bulkUpdateEnabled, editableStocks]);

  const handleNewStockChange = useCallback(
    (materialId, combinationId, uniqueKey, field, value) => {
      setEditableStocks((prev) =>
        prev.map((group) =>
          group.materialId === materialId
            ? {
                ...group,
                combinations: group.combinations.map((comb) =>
                  comb.combinationId === combinationId
                    ? {
                        ...comb,
                        warehouseStocks: comb.warehouseStocks.map((stock) => {
                          if (stock.uniqueKey === uniqueKey) {
                            let updatedValue = value;

                            if (field === "quantity") {
                              updatedValue = Number(value) || 0;
                            } else if (field === "warehouseId") {
                              // Convert to number, or keep as empty string
                              updatedValue = value === "" ? "" : Number(value);
                            }

                            return {
                              ...stock,
                              [field]: updatedValue,
                            };
                          }
                          return stock;
                        }),
                      }
                    : comb,
                ),
              }
            : group,
        ),
      );
    },
    [],
  );

  const handleQuickQuantityChange = (
    materialId,
    combinationId,
    uniqueKey,
    increment,
    max = null,
  ) => {
    setEditableStocks((prev) =>
      prev.map((group) =>
        group.materialId === materialId
          ? {
              ...group,
              combinations: group.combinations.map((comb) =>
                comb.combinationId === combinationId
                  ? {
                      ...comb,
                      warehouseStocks: comb.warehouseStocks.map((stock) => {
                        if (stock.uniqueKey === uniqueKey) {
                          const currentUpdate =
                            parseInt(stock.quantityUpdate, 10) || 0;
                          let newUpdate = Math.max(
                            0,
                            currentUpdate + increment,
                          );
                          if (max !== null)
                            newUpdate = Math.min(newUpdate, max);
                          return {
                            ...stock,
                            quantityUpdate: newUpdate.toString(),
                          };
                        }
                        return stock;
                      }),
                    }
                  : comb,
              ),
            }
          : group,
      ),
    );
  };

  const handleAddWarehouse = useCallback(
    (materialId, combinationId, materialType) => {
      const timestamp = Date.now();
      const newStock = {
        id: `new-${timestamp}`,
        uniqueKey: generateUniqueStockKey(
          materialId,
          `new-${timestamp}`,
          "",
          combinationId,
          true,
        ),
        warehouseId: "",
        rackNumber: "",
        quantity: 0,
        unit: "PIECE",
        combinationId,
        combinationDisplayName: null,
        combinationSku: null,
        isNew: true,
        originalQuantity: 0,
        quantityUpdate: "",
        rawMaterialId: materialType === "raw-material" ? materialId : 0,
        productId: materialType === "product" ? materialId : 0,
      };

      setEditableStocks((prev) =>
        prev.map((group) =>
          group.materialId === materialId
            ? {
                ...group,
                combinations: group.combinations.map((comb) =>
                  comb.combinationId === combinationId
                    ? {
                        ...comb,
                        warehouseStocks: [...comb.warehouseStocks, newStock],
                      }
                    : comb,
                ),
              }
            : group,
        ),
      );

      if (!bulkUpdateEnabled) setBulkUpdateEnabled(true);
    },
    [bulkUpdateEnabled, generateUniqueStockKey],
  );

  const handleRemoveWarehouse = useCallback(
    (materialId, combinationId, uniqueKey) => {
      setEditableStocks((prev) =>
        prev.map((group) =>
          group.materialId === materialId
            ? {
                ...group,
                combinations: group.combinations.map((comb) =>
                  comb.combinationId === combinationId
                    ? {
                        ...comb,
                        warehouseStocks: comb.warehouseStocks.filter(
                          (stock) => stock.uniqueKey !== uniqueKey,
                        ),
                      }
                    : comb,
                ),
              }
            : group,
        ),
      );
    },
    [],
  );

  // Switching type clears anything typed for the previous type (and unsaved new rows)
  const handleTransactionTypeChange = (type) => {
    if (type === transactionType) return;
    setTransactionType(type);
    setEditableStocks((prev) =>
      prev.map((group) => ({
        ...group,
        combinations: group.combinations.map((comb) => ({
          ...comb,
          warehouseStocks: comb.warehouseStocks
            .filter((stock) => !stock.isNew)
            .map((stock) => ({
              ...stock,
              quantityUpdate: "",
              toWarehouseId: "",
              toRackNumber: "",
            })),
        })),
      })),
    );
  };

  const handleBulkUpdate = async () => {
    if (!bulkUpdateEnabled) {
      setBulkUpdateEnabled(true);
      await loadMaterials();
      return;
    }

    const payload = [];
    let hasErrors = false;
    const fail = (msg) => {
      if (!hasErrors) toast.error(msg);
      hasErrors = true;
    };

    editableStocks.forEach((group) => {
      group.combinations.forEach((comb) => {
        comb.warehouseStocks.forEach((stock) => {
          const base = {
            combinationId: parseInt(comb.combinationId, 10) || 0,
            productId:
              group.type === "product" ? parseInt(group.materialId, 10) : 0,
            rawMaterialId:
              group.type === "raw-material"
                ? parseInt(group.materialId, 10)
                : 0,
            remarks: stock.remarks || "",
            transactionType,
          };

          // Brand-new warehouse/rack rows: INBOUND only
          if (stock.isNew) {
            if (transactionType !== "INBOUND") return;
            const qty = Number(stock.quantity) || 0;
            if (!stock.warehouseId || qty <= 0 || !stock.rackNumber?.trim()) {
              fail("New stock needs a warehouse, rack number and quantity.");
              return;
            }
            payload.push({
              ...base,
              warehouseId: parseInt(stock.warehouseId, 10),
              quantity: qty,
              rackNumber: stock.rackNumber.trim(),
            });
            return;
          }

          const qty = Number(stock.quantityUpdate) || 0;
          if (qty <= 0) return; // untouched

          if (transactionType === "INBOUND") {
            payload.push({
              ...base,
              warehouseId: parseInt(stock.warehouseId, 10),
              quantity: qty,
              rackNumber: stock.rackNumber || "",
            });
            return;
          }

          // OUTBOUND / TRANSFER need existing stock and cannot exceed it
          if (qty > (Number(stock.originalQuantity) || 0)) {
            fail(
              `Quantity exceeds available stock in ${stock.warehouseName || "warehouse"}.`,
            );
            return;
          }

          if (transactionType === "OUTBOUND") {
            // Backend identifies the source by warehouse + rack (no warehouseStockHistoryId)
            payload.push({
              ...base,
              warehouseId: parseInt(stock.warehouseId, 10),
              quantity: qty,
              rackNumber: stock.rackNumber || "",
            });
            return;
          }

          // TRANSFER
          const toWarehouseId = parseInt(stock.toWarehouseId, 10);
          const toRack = (stock.toRackNumber || "").trim();
          if (!toWarehouseId || !toRack) {
            fail("Transfers need a destination warehouse and rack.");
            return;
          }
          if (
            toWarehouseId === Number(stock.warehouseId) &&
            toRack === (stock.rackNumber || "")
          ) {
            fail("Transfer destination is the same as the source.");
            return;
          }
          payload.push({
            ...base,
            warehouseId: parseInt(stock.warehouseId, 10),
            fromWarehouseId: parseInt(stock.warehouseId, 10),
            toWarehouseId,
            fromRackNumber: stock.rackNumber || "",
            toRackNumber: toRack,
            warehouseStockHistoryId: stock.id,
            quantity: qty,
          });
        });
      });
    });

    if (hasErrors) return;

    if (payload.length === 0) {
      toast.info("No changes to submit.");
      return;
    }

    try {
      const endpoint =
        activeTab === "raw-materials"
          ? "/warehouse-stock/bulk/transaction-type/raw-material"
          : "/warehouse-stock/bulk/transaction-type/product";

      await axiosInstance.post(`${getApiBaseUrl()}${endpoint}`, payload);

      toast.success(
        `${transactionType.charAt(0) + transactionType.slice(1).toLowerCase()} saved for ${payload.length} record${payload.length !== 1 ? "s" : ""}`,
      );
      setBulkUpdateEnabled(false);

      await loadMaterials();
      markPageAsCompleted("Stock Levels");
    } catch (err) {
      console.error("Error updating stocks:", err);
    }
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find((w) => w.id === warehouseId);
    return warehouse ? warehouse.name : "N/A";
  };

  const toggleGroup = useCallback((materialId) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(materialId)) {
        newSet.delete(materialId);
      } else {
        newSet.add(materialId);
      }
      return newSet;
    });
  }, []);

  // Table structure calculation
  const tableStructure = useMemo(() => {
    if (editableStocks.length === 0) return null;

    const allWarehouseIds = new Set();

    editableStocks.forEach((group) => {
      group.combinations.forEach((comb) => {
        comb.warehouseStocks.forEach((stock) => {
          if (
            stock.warehouseId !== "" &&
            stock.warehouseId !== null &&
            stock.warehouseId !== undefined
          ) {
            // Ensure we're working with numbers
            const numericId = Number(stock.warehouseId);
            if (!isNaN(numericId) && numericId > 0) {
              allWarehouseIds.add(numericId);
            }
          }
        });
      });
    });

    const uniqueWarehouses = Array.from(allWarehouseIds)
      .map((id) => warehouses.find((w) => w.id === id))
      .filter(Boolean)
      .sort((a, b) => a.id - b.id); // Sort by ID for consistency

    return { uniqueWarehouses };
  }, [editableStocks, warehouses]);

  // Render table headers
  const renderTableHeaders = useCallback(() => {
    if (!tableStructure) return null;

    const { uniqueWarehouses } = tableStructure;

    return (
      <>
        <tr className="group-header-row">
          <th className="fixed-column material-column group-header" rowSpan="2">
            Material
          </th>

          <th className="basic-info group-header" rowSpan="2">
            Total Stock
          </th>

          {uniqueWarehouses.length > 0 && (
            <th
              className="warehouse-header group-header warehouse-group"
              colSpan={uniqueWarehouses.length * 2}
            >
              📦 Warehouse Stock Locations
              <span className="status-indicator">
                {uniqueWarehouses.length}
              </span>
            </th>
          )}
        </tr>

        <tr className="individual-header-row">
          {uniqueWarehouses.map((warehouse) => (
            <React.Fragment key={`warehouse-${warehouse.id}`}>
              <th className="warehouse-header sub-header">
                {warehouse.name} - Quantity
              </th>
              <th className="warehouse-header sub-header">
                {warehouse.name} - Rack
              </th>
            </React.Fragment>
          ))}
        </tr>
      </>
    );
  }, [tableStructure]);

  // Render table rows
  const renderTableRows = useCallback(() => {
    if (!editableStocks || editableStocks.length === 0 || !tableStructure)
      return null;

    const { uniqueWarehouses } = tableStructure;

    return editableStocks.flatMap((group) => {
      const isExpanded = expandedGroups.has(group.materialId);
      const rows = [];

      // Group header row
      rows.push(
        <tr
          key={`group-${group.materialId}`}
          className="material-group-row"
          onClick={() => toggleGroup(group.materialId)}
          style={{
            cursor: "pointer",
            background: "#f8fafc",
            fontWeight: "600",
          }}
        >
          <td className="fixed-column" style={{ padding: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontSize: "1.2rem" }}>
                {isExpanded ? "▼" : "▶"}
              </span>
              <span>{group.materialName}</span>
              <span
                style={{
                  fontSize: "0.85rem",
                  color: "#64748b",
                  fontWeight: "normal",
                }}
              >
                ({group.combinations.length} variant
                {group.combinations.length !== 1 ? "s" : ""})
              </span>
              <button
                type="button"
                className="qr-trigger"
                title="Show QR code"
                onClick={(e) => {
                  e.stopPropagation();
                  openGroupQR(group);
                }}
              >
                ▦ QR
              </button>
            </div>
          </td>
          <td style={{ fontWeight: "600" }}>{group.totalMaterialStock}</td>
          {uniqueWarehouses.map((warehouse) => (
            <React.Fragment
              key={`empty-group-${group.materialId}-${warehouse.id}`}
            >
              <td></td>
              <td></td>
              <td></td>
            </React.Fragment>
          ))}
        </tr>,
      );

      if (isExpanded) {
        group.combinations.forEach((comb) => {
          const displayName =
            comb.combinationDisplayName || comb.combinationSku;

          rows.push(
            <tr key={`comb-${group.materialId}-${comb.combinationId}`}>
              <td className="fixed-column" style={{ paddingLeft: "48px" }}>
                <span className="readonly-value">
                  {displayName ? `${displayName}` : "Default Variant"}
                </span>{" "}
                <button
                  type="button"
                  className="qr-trigger"
                  title="Show QR code"
                  onClick={() => openVariantQR(group, comb)}
                >
                  ▦ QR
                </button>
              </td>

              <td className="basic-info">
                <span className="readonly-value">{comb.totalStock}</span>
              </td>

              {uniqueWarehouses.map((warehouse) => {
                const stock = comb.warehouseStocks.find((s) => {
                  // Convert both to numbers for comparison
                  const stockWarehouseId = Number(s.warehouseId);
                  const warehouseId = Number(warehouse.id);

                  // Match if both are valid numbers and equal
                  return (
                    !isNaN(stockWarehouseId) &&
                    !isNaN(warehouseId) &&
                    stockWarehouseId === warehouseId
                  );
                });

                if (!stock) {
                  return (
                    <React.Fragment key={`empty-${warehouse.id}`}>
                      <td className="empty-cell">-</td>
                      <td className="empty-cell">-</td>
                    </React.Fragment>
                  );
                }

                if (stock.isNew && stock.warehouseId) {
                  return (
                    <React.Fragment key={`new-${stock.uniqueKey}`}>
                      <td className="warehouse-cell new-stock-cell">
                        <input
                          type="number"
                          value={stock.quantity}
                          onChange={(e) =>
                            handleNewStockChange(
                              group.materialId,
                              comb.combinationId,
                              stock.uniqueKey,
                              "quantity",
                              e.target.value,
                            )
                          }
                          min="0"
                          placeholder="Quantity"
                          style={{ width: "100px" }}
                        />
                        <select
                          value={stock.unit || "PIECE"}
                          onChange={(e) =>
                            handleNewStockChange(
                              group.materialId,
                              comb.combinationId,
                              stock.uniqueKey,
                              "unit",
                              e.target.value,
                            )
                          }
                          style={{ width: "100px", marginTop: "4px" }}
                        >
                          {UNITS.map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="warehouse-cell new-stock-cell">
                        <input
                          type="text"
                          value={stock.rackNumber}
                          onChange={(e) =>
                            handleNewStockChange(
                              group.materialId,
                              comb.combinationId,
                              stock.uniqueKey,
                              "rackNumber",
                              e.target.value,
                            )
                          }
                          placeholder="Rack"
                          style={{ width: "80px" }}
                        />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveWarehouse(
                              group.materialId,
                              comb.combinationId,
                              stock.uniqueKey,
                            );
                          }}
                          style={{
                            marginLeft: "8px",
                            padding: "4px 8px",
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "0.75rem",
                          }}
                          title="Remove"
                        >
                          ✕
                        </button>
                      </td>
                    </React.Fragment>
                  );
                }

                const hasZeroQuantity = stock.originalQuantity === 0;
                const currentUpdate = parseInt(stock.quantityUpdate, 10) || 0;
                const newQty = Math.max(
                  0,
                  (stock.originalQuantity || 0) + currentUpdate,
                );

                return (
                  <React.Fragment key={stock.uniqueKey}>
                    <td className="warehouse-cell editable-cell">
                      {bulkUpdateEnabled ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <span
                            className="readonly-value"
                            style={{ fontSize: "0.85rem", color: "#64748b" }}
                          >
                            Current: {stock.originalQuantity}
                          </span>
                          <input
                            type="number"
                            value={stock.quantityUpdate}
                            onChange={(e) =>
                              handleQuantityUpdate(
                                group.materialId,
                                comb.combinationId,
                                stock.uniqueKey,
                                e.target.value,
                              )
                            }
                            placeholder={
                              hasZeroQuantity ? "Add qty" : "+/- amount"
                            }
                            style={{ width: "100px" }}
                          />
                          {stock.quantityUpdate !== "" &&
                            stock.quantityUpdate !== "0" && (
                              <span
                                style={{
                                  fontSize: "0.8rem",
                                  fontWeight: "600",
                                  color:
                                    currentUpdate > 0 ? "#10b981" : "#ef4444",
                                }}
                              >
                                New: {newQty}
                              </span>
                            )}
                        </div>
                      ) : (
                        <span className="readonly-value">
                          {stock.originalQuantity}
                        </span>
                      )}
                    </td>
                    <td className="warehouse-cell">
                      <span className="readonly-value">
                        {stock.rackNumber || "-"}
                      </span>
                    </td>
                  </React.Fragment>
                );
              })}
            </tr>,
          );

          const unassignedStocks = comb.warehouseStocks.filter((s) => {
            if (!s.isNew) return false;

            const warehouseId = Number(s.warehouseId);
            return (
              s.warehouseId === "" ||
              s.warehouseId === null ||
              s.warehouseId === undefined ||
              isNaN(warehouseId) ||
              warehouseId === 0
            );
          });

          if (bulkUpdateEnabled && unassignedStocks.length > 0) {
            unassignedStocks.forEach((newStock) => {
              rows.push(
                <tr
                  key={`unassigned-${group.materialId}-${comb.combinationId}-${newStock.uniqueKey}`}
                  style={{ background: "#fef3c7" }}
                >
                  <td
                    className="fixed-column"
                    style={{ paddingLeft: "48px", background: "#fef3c7" }}
                  >
                    <span
                      style={{
                        fontStyle: "italic",
                        color: "#d97706",
                        fontSize: "0.9rem",
                        fontWeight: "600",
                      }}
                    >
                      ⚠️ New Stock Entry
                    </span>
                  </td>
                  <td style={{ background: "#fef3c7" }}>
                    <span style={{ color: "#d97706", fontSize: "0.85rem" }}>
                      Pending
                    </span>
                  </td>

                  <td
                    className="warehouse-cell new-stock-cell"
                    colSpan={uniqueWarehouses.length * 2}
                    style={{ padding: "12px" }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "2fr 1.5fr 1fr 0.5fr",
                        gap: "12px",
                        alignItems: "center",
                      }}
                    >
                      {/* Warehouse Dropdown */}
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            color: "#d97706",
                            marginBottom: "4px",
                          }}
                        >
                          Select Warehouse *
                        </label>
                        <select
                          value={newStock.warehouseId}
                          onChange={(e) => {
                            console.log("Warehouse selected:", {
                              value: e.target.value,
                              asNumber: Number(e.target.value),
                              stockBefore: newStock,
                            });
                            handleNewStockChange(
                              group.materialId,
                              comb.combinationId,
                              newStock.uniqueKey,
                              "warehouseId",
                              e.target.value,
                            );
                          }}
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "2px solid #f59e0b",
                            borderRadius: "6px",
                            fontSize: "0.9rem",
                            background: "white",
                          }}
                        >
                          <option value="">-- Select Warehouse --</option>
                          {warehouses.map((w) => (
                            <option key={w.id} value={w.id}>
                              {w.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Quantity Input */}
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            color: "#d97706",
                            marginBottom: "4px",
                          }}
                        >
                          Quantity *
                        </label>
                        <input
                          type="number"
                          value={
                            newStock.quantity === 0 ? "" : newStock.quantity
                          }
                          onChange={(e) =>
                            handleNewStockChange(
                              group.materialId,
                              comb.combinationId,
                              newStock.uniqueKey,
                              "quantity",
                              e.target.value,
                            )
                          }
                          min="0"
                          placeholder="Enter qty"
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "2px solid #f59e0b",
                            borderRadius: "6px",
                            fontSize: "0.9rem",
                          }}
                        />
                      </div>

                      {/* Unit Dropdown */}
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            color: "#d97706",
                            marginBottom: "4px",
                          }}
                        >
                          Unit *
                        </label>
                        <select
                          value={newStock.unit || "PIECE"}
                          onChange={(e) =>
                            handleNewStockChange(
                              group.materialId,
                              comb.combinationId,
                              newStock.uniqueKey,
                              "unit",
                              e.target.value,
                            )
                          }
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "2px solid #f59e0b",
                            borderRadius: "6px",
                            fontSize: "0.9rem",
                            background: "white",
                          }}
                        >
                          {UNITS.map((unit) => (
                            <option key={unit.value} value={unit.value}>
                              {unit.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Rack Number Input */}
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "0.75rem",
                            fontWeight: "600",
                            color: "#d97706",
                            marginBottom: "4px",
                          }}
                        >
                          Rack
                        </label>
                        <input
                          type="text"
                          value={newStock.rackNumber}
                          onChange={(e) =>
                            handleNewStockChange(
                              group.materialId,
                              comb.combinationId,
                              newStock.uniqueKey,
                              "rackNumber",
                              e.target.value,
                            )
                          }
                          placeholder="e.g. A1"
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "2px solid #f59e0b",
                            borderRadius: "6px",
                            fontSize: "0.9rem",
                          }}
                        />
                      </div>

                      {/* Remove Button */}
                      <div style={{ display: "flex", alignItems: "flex-end" }}>
                        <button
                          onClick={() =>
                            handleRemoveWarehouse(
                              group.materialId,
                              comb.combinationId,
                              newStock.uniqueKey,
                            )
                          }
                          style={{
                            padding: "8px 12px",
                            background: "#ef4444",
                            color: "white",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            fontWeight: "500",
                          }}
                          title="Remove this stock entry"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>,
              );
            });
          }

          // Add "Add Warehouse" button row after each combination
          if (bulkUpdateEnabled) {
            rows.push(
              <tr
                key={`add-warehouse-${group.materialId}-${comb.combinationId}`}
              >
                <td
                  colSpan={2 + uniqueWarehouses.length * 2}
                  style={{
                    padding: "8px 48px",
                    background: "#f8fafc",
                    borderBottom: "2px solid #e2e8f0",
                  }}
                >
                  <ButtonWithIcon
                    icon="lucide:plus"
                    text="Add Warehouse Stock"
                    onClick={() =>
                      handleAddWarehouse(
                        group.materialId,
                        comb.combinationId,
                        group.type,
                      )
                    }
                    className="add-warehouse-button"
                    variant="secondary"
                    style={{
                      fontSize: "0.85rem",
                      padding: "6px 12px",
                    }}
                  />
                </td>
              </tr>,
            );
          }
        });
      }
      return rows;
    });
  }, [
    editableStocks,
    tableStructure,
    expandedGroups,
    bulkUpdateEnabled,
    handleQuantityUpdate,
    handleNewStockChange,
    handleRemoveWarehouse,
    handleAddWarehouse,
    toggleGroup,
    warehouses,
    openGroupQR,
    openVariantQR,
  ]);

  // Racks already used per warehouse on this page -> shown as rack buttons
  const rackMap = {};
  editableStocks.forEach((g) =>
    g.combinations.forEach((c) =>
      c.warehouseStocks.forEach((st) => {
        if (st.isNew || !st.rackNumber || !st.warehouseId) return;
        const key = String(st.warehouseId);
        if (!(rackMap[key] || []).includes(st.rackNumber))
          rackMap[key] = [...(rackMap[key] || []), st.rackNumber];
      }),
    ),
  );
  const warehouseChips = warehouses.map((w) => ({
    value: w.id,
    label: w.name,
  }));

  // Per-stock edit controls, depends on the selected transaction type
  const renderEditControls = (group, comb, stock) => {
    const original = Number(stock.originalQuantity) || 0;
    const qty = Number(stock.quantityUpdate) || 0;
    const cap = transactionType === "INBOUND" ? null : original;
    const bump = (n) =>
      handleQuickQuantityChange(
        group.materialId,
        comb.combinationId,
        stock.uniqueKey,
        n,
        cap,
      );
    const setQty = (raw) => {
      let v = Math.max(0, Number(raw) || 0);
      if (cap !== null) v = Math.min(v, cap);
      handleQuantityUpdate(
        group.materialId,
        comb.combinationId,
        stock.uniqueKey,
        raw === "" ? "" : String(v),
      );
    };
    const setField = (field, value) =>
      handleNewStockChange(
        group.materialId,
        comb.combinationId,
        stock.uniqueKey,
        field,
        value,
      );

    const qtyInput = (placeholder) => (
      <input
        type="number"
        min="0"
        value={stock.quantityUpdate}
        onChange={(e) => setQty(e.target.value)}
        placeholder={placeholder}
        className="adjust-input"
      />
    );

    if (transactionType === "INBOUND") {
      return (
        <div className="edit-mini">
          <div className="adjust-btns">
            <button
              type="button"
              className="adjust-btn--add"
              onClick={() => bump(1)}
            >
              +1
            </button>
            <button
              type="button"
              className="adjust-btn--add"
              onClick={() => bump(10)}
            >
              +10
            </button>
            <button
              type="button"
              className="adjust-btn--add"
              onClick={() => bump(100)}
            >
              +100
            </button>
            {qtyInput("Add")}
          </div>
          {qty > 0 && <span className="new-qty-mini">→ {original + qty}</span>}
        </div>
      );
    }

    if (transactionType === "OUTBOUND") {
      return (
        <div className="edit-mini">
          <div className="adjust-btns">
            <button
              type="button"
              className="adjust-btn--remove"
              onClick={() => bump(1)}
              disabled={original === 0}
            >
              −1
            </button>
            <button
              type="button"
              className="adjust-btn--remove"
              onClick={() => bump(10)}
              disabled={original === 0}
            >
              −10
            </button>
            <button
              type="button"
              className="adjust-btn--remove"
              onClick={() => setQty(String(original))}
              disabled={original === 0}
            >
              All
            </button>
            {qtyInput("Remove")}
          </div>
          {qty > 0 && <span className="new-qty-mini">→ {original - qty}</span>}
          {original === 0 && (
            <span className="txn-note">No stock to take out</span>
          )}
        </div>
      );
    }

    // TRANSFER
    return (
      <div className="edit-mini">
        {original === 0 ? (
          <span className="txn-note">No stock to transfer</span>
        ) : (
          <>
            <div className="adjust-btns">
              {qtyInput("Qty")}
              <button
                type="button"
                className="adjust-btn--move"
                onClick={() => setQty(String(original))}
              >
                All
              </button>
            </div>
            <div className="chip-stack">
              <span className="chip-label">
                Move to warehouse (existing or new)
              </span>
              <WarehousePicker
                warehouses={warehouseChips}
                usedIds={comb.warehouseStocks
                  .filter((st) => !st.isNew)
                  .map((st) => st.warehouseId)}
                value={stock.toWarehouseId || ""}
                size="sm"
                tone="blue"
                onChange={(v) => {
                  setField("toWarehouseId", v);
                  setField("toRackNumber", "");
                }}
              />
              {stock.toWarehouseId ? (
                <>
                  <span className="chip-label">
                    Move to rack (existing or new)
                  </span>
                  <RackPicker
                    key={`to-${stock.uniqueKey}-${stock.toWarehouseId}`}
                    racks={rackMap[String(stock.toWarehouseId)] || []}
                    value={stock.toRackNumber || ""}
                    size="sm"
                    tone="blue"
                    onChange={(v) => setField("toRackNumber", v)}
                  />
                </>
              ) : null}
            </div>
            {qty > 0 && (
              <span className="new-qty-mini">
                {original} → {original - qty}
              </span>
            )}
          </>
        )}
      </div>
    );
  };

  if (!canAccessStockUpdate()) return <AccessDenied />;

  return (
    <div className="stock-update">
      {/* Metric summary tiles - derived from real stock data */}
      <div className="stock-update__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">
              {activeTab === "raw-materials"
                ? "Materials Tracked"
                : "Products Tracked"}
            </span>
            <span className="metric-tile__value">{stockStats.totalItems}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--neutral">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Stock Quantity</span>
            <span className="metric-tile__value">
              {stockStats.totalQuantity.toLocaleString()}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M20.6 12.6 12.6 4.6a2 2 0 0 0-1.4-.6H5a1 1 0 0 0-1 1v6.2a2 2 0 0 0 .6 1.4l8 8a2 2 0 0 0 2.8 0l5.2-5.2a2 2 0 0 0 0-2.8Z"
              />
              <circle cx="8" cy="8" r="1" />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Warehouses in Use</span>
            <span className="metric-tile__value">
              {stockStats.warehousesInUse}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--info">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 21h18M5 21V7l7-4 7 4v14M9 9h.01M9 13h.01M9 17h.01M15 9h.01M15 13h.01M15 17h.01"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Zero Stock Items</span>
            <span className="metric-tile__value">
              {stockStats.zeroStockItems}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--warning">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="m9 9 6 6M15 9l-6 6" />
            </svg>
          </div>
        </div>
      </div>

      <div className="stock-update__actions">
        <button
          type="button"
          className={`action-card ${bulkUpdateEnabled ? "action-card--success" : ""}`}
          onClick={handleBulkUpdate}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={bulkUpdateEnabled ? "M5 13l4 4L19 7" : "M12 4v16m8-8H4"}
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {bulkUpdateEnabled
                ? "Save All Changes"
                : "Enable Quantity Update"}
            </span>
            <span className="action-card-desc">
              {bulkUpdateEnabled
                ? "Submit the quantity changes you've made"
                : "Edit stock quantities"}
            </span>
          </span>
        </button>

        <div className="stock-update__tab-navigation">
          <ButtonWithIcon
            icon="iconamoon:category-light"
            text="Raw Materials"
            onClick={() => setActiveTab("raw-materials")}
            className={`tab-button ${
              activeTab === "raw-materials" ? "active" : ""
            }`}
          />
          <ButtonWithIcon
            icon="fluent:box-24-regular"
            text="Products"
            onClick={() => setActiveTab("products")}
            className={`tab-button ${activeTab === "products" ? "active" : ""}`}
          />
        </div>

        <div className="stock-update__view-toggle">
          <ButtonWithIcon
            icon="fluent:grid-24-regular"
            text=""
            title="Card View"
            aria-label="Card View"
            onClick={() => setViewMode("cards")}
            className={`view-toggle-button ${
              viewMode === "cards" ? "active" : ""
            }`}
          />
          <ButtonWithIcon
            icon="fluent:table-24-regular"
            text=""
            title="Table View"
            aria-label="Table View"
            onClick={() => {
              if (!bulkUpdateEnabled) {
                setViewMode("table");
              } else {
                toast.info(
                  "Please use Card View for editing. Table view is for viewing only.",
                );
              }
            }}
            className={`view-toggle-button ${
              viewMode === "table" ? "active" : ""
            } ${bulkUpdateEnabled ? "disabled" : ""}`}
            disabled={bulkUpdateEnabled}
          />
        </div>

        <button
          type="button"
          className="qr-trigger qr-trigger--scan"
          onClick={() => setShowScanner(true)}
        >
          📷 Scan QR
        </button>
      </div>

      <div
        className={`stock-update__layout ${isSidebarCollapsed ? "stock-update__layout--collapsed" : ""}`}
      >
        <div className="stock-update__main">
          {bulkUpdateEnabled && (
            <div
              className="txn-selector"
              role="tablist"
              aria-label="Transaction type"
            >
              {[
                { id: "INBOUND", label: "Inbound / Add Stock" },
                { id: "OUTBOUND", label: "Outbound / Remove Stock" },
                { id: "TRANSFER", label: "Transfer / Rerack Stock" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  role="tab"
                  aria-selected={transactionType === t.id}
                  className={`txn-option txn-option--${t.id.toLowerCase()} ${
                    transactionType === t.id ? "active" : ""
                  }`}
                  onClick={() => handleTransactionTypeChange(t.id)}
                >
                  <strong>{t.label}</strong>
                </button>
              ))}
            </div>
          )}
          {bulkUpdateEnabled && (
            <div className="stock-update__instructions">
              <div className="instruction-content">
                <i className="fas fa-info-circle" />
                <div>
                  <p>
                    {viewMode === "cards"
                      ? "Click on a material to expand/collapse variants. Choose Inbound, Outbound or Transfer above, then enter quantities per warehouse."
                      : "Click on a material to expand/collapse variants. Edit stock quantities directly in the table cells. Use +/- values for adjustments (positive to add, negative to subtract). For stocks with 0 quantity, enter a positive number to add stock. Blue columns indicate warehouse stock locations."}
                  </p>
                </div>
              </div>
            </div>
          )}
          {!bulkUpdateEnabled && (
            <div className="stock-update__filters">
              <div className="filter-group">
                <input
                  type="text"
                  placeholder="🔍 Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
              <div className="filter-group">
                <select
                  value={warehouseFilter}
                  onChange={(e) => setWarehouseFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Warehouses</option>
                  {warehouses.map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <select
                  value={stockFilter}
                  onChange={(e) => setStockFilter(e.target.value)}
                  className="filter-select"
                >
                  <option value="all">All Stock Levels</option>
                  <option value="in-stock">In Stock Only</option>
                  <option value="low-stock">Low Stock</option>
                  <option value="out-of-stock">Out of Stock</option>
                </select>
              </div>
            </div>
          )}
          {viewMode === "cards" ? (
            <div className="stock-update__cards-container">
              {editableStocks.length > 0 ? (
                <div className="stock-cards-compact">
                  {editableStocks.map((group) => {
                    const isExpanded = expandedGroups.has(group.materialId);

                    return (
                      <div
                        key={`group-${group.materialId}`}
                        className="product-card-compact"
                      >
                        {/* LEVEL 1: Product Header - Ultra Compact */}
                        <div
                          className="product-header-compact"
                          onClick={() => toggleGroup(group.materialId)}
                        >
                          <div className="header-left">
                            <span
                              className={`arrow ${isExpanded ? "expanded" : ""}`}
                            >
                              ▶
                            </span>
                            <div className="product-info">
                              <span className="product-name">
                                {group.materialName}
                              </span>
                            </div>
                          </div>
                          <div className="header-right">
                            <button
                              type="button"
                              className="qr-trigger"
                              title="Show QR code"
                              onClick={(e) => {
                                e.stopPropagation();
                                openGroupQR(group);
                              }}
                            >
                              ▦ QR
                            </button>
                            <span className="variants-badge">
                              {group.combinations.length} variants
                            </span>
                            <span className="stock-badge">
                              {group.totalMaterialStock}
                            </span>
                          </div>
                        </div>

                        {/* LEVEL 2: Variants - Only when expanded */}
                        {isExpanded && (
                          <div className="variants-compact">
                            {group.combinations.map((comb) => {
                              return (
                                <div
                                  key={`comb-${comb.combinationId}`}
                                  className="variant-row-compact"
                                >
                                  {/* Variant Info */}
                                  <div className="variant-info-compact">
                                    <span className="variant-label">
                                      {comb.combinationDisplayName ||
                                        comb.combinationSku ||
                                        "Default"}
                                    </span>
                                    <span
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <button
                                        type="button"
                                        className="qr-trigger"
                                        title="Show QR code"
                                        onClick={() =>
                                          openVariantQR(group, comb)
                                        }
                                      >
                                        ▦ QR
                                      </button>
                                      <span className="variant-stock">
                                        {comb.totalStock}
                                      </span>
                                    </span>
                                  </div>

                                  {/* LEVEL 3: Warehouse Locations - Horizontal Pills */}
                                  <div className="warehouses-horizontal">
                                    {comb.warehouseStocks.map((stock) => (
                                      <div
                                        key={stock.uniqueKey}
                                        className={`warehouse-pill ${
                                          stock.isNew ? "new" : ""
                                        }`}
                                      >
                                        {stock.isNew ? (
                                          // NEW STOCK - Inline form
                                          <div className="new-stock-inline">
                                            <div className="chip-stack">
                                              <span className="chip-label">
                                                Warehouse (existing or new)
                                              </span>
                                              <WarehousePicker
                                                warehouses={warehouseChips}
                                                usedIds={comb.warehouseStocks
                                                  .filter((st) => !st.isNew)
                                                  .map((st) => st.warehouseId)}
                                                value={stock.warehouseId}
                                                size="sm"
                                                tone="green"
                                                onChange={(v) => {
                                                  handleNewStockChange(
                                                    group.materialId,
                                                    comb.combinationId,
                                                    stock.uniqueKey,
                                                    "warehouseId",
                                                    v,
                                                  );
                                                  handleNewStockChange(
                                                    group.materialId,
                                                    comb.combinationId,
                                                    stock.uniqueKey,
                                                    "rackNumber",
                                                    "",
                                                  );
                                                }}
                                              />
                                              {stock.warehouseId ? (
                                                <>
                                                  <span className="chip-label">
                                                    Rack (existing or new)
                                                  </span>
                                                  <RackPicker
                                                    key={`new-${stock.uniqueKey}-${stock.warehouseId}`}
                                                    racks={
                                                      rackMap[
                                                        String(
                                                          stock.warehouseId,
                                                        )
                                                      ] || []
                                                    }
                                                    value={
                                                      stock.rackNumber || ""
                                                    }
                                                    size="sm"
                                                    tone="green"
                                                    onChange={(v) =>
                                                      handleNewStockChange(
                                                        group.materialId,
                                                        comb.combinationId,
                                                        stock.uniqueKey,
                                                        "rackNumber",
                                                        v,
                                                      )
                                                    }
                                                  />
                                                </>
                                              ) : null}
                                            </div>
                                            <input
                                              type="number"
                                              value={
                                                stock.quantity === 0
                                                  ? ""
                                                  : stock.quantity
                                              }
                                              onChange={(e) =>
                                                handleNewStockChange(
                                                  group.materialId,
                                                  comb.combinationId,
                                                  stock.uniqueKey,
                                                  "quantity",
                                                  e.target.value,
                                                )
                                              }
                                              placeholder="Qty"
                                              className="mini-input"
                                            />
                                            <button
                                              onClick={() =>
                                                handleRemoveWarehouse(
                                                  group.materialId,
                                                  comb.combinationId,
                                                  stock.uniqueKey,
                                                )
                                              }
                                              className="remove-mini"
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        ) : (
                                          // EXISTING STOCK - Compact display
                                          <>
                                            <div className="warehouse-name-mini">
                                              📦{" "}
                                              {getWarehouseName(
                                                stock.warehouseId,
                                              )}
                                            </div>
                                            <div className="stock-details-mini">
                                              <span className="qty">
                                                {stock.originalQuantity}{" "}
                                                {stock.unit}
                                              </span>
                                              {stock.rackNumber && (
                                                <span className="rack">
                                                  📍 {stock.rackNumber}
                                                </span>
                                              )}
                                            </div>

                                            {/* Edit Mode - controls depend on transaction type */}
                                            {bulkUpdateEnabled &&
                                              renderEditControls(
                                                group,
                                                comb,
                                                stock,
                                              )}
                                          </>
                                        )}
                                      </div>
                                    ))}

                                    {/* Add Warehouse Button - Only if less than 3 */}
                                    {bulkUpdateEnabled &&
                                      transactionType === "INBOUND" &&
                                      comb.warehouseStocks.length < 3 && (
                                        <button
                                          onClick={() =>
                                            handleAddWarehouse(
                                              group.materialId,
                                              comb.combinationId,
                                              group.type,
                                            )
                                          }
                                          className="add-warehouse-mini"
                                        >
                                          + Add
                                        </button>
                                      )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="stock-update__empty-state">
                  <i className="fas fa-box-open stock-update__empty-icon" />
                  <h3>No Stock Data Available</h3>
                  <p>
                    No{" "}
                    {activeTab === "raw-materials"
                      ? "raw materials"
                      : "products"}{" "}
                    stock found.
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="stock-update__table-container">
              {editableStocks.length > 0 ? (
                <div className="stock-table-results">
                  <div
                    style={{
                      padding: "20px 24px",
                      borderBottom: "1px solid #e2e8f0",
                      background: "#f8fafc",
                    }}
                  >
                    <h3
                      style={{
                        margin: 0,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        color: "#1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      📊 Stock Overview
                      <span className="status-indicator success">
                        {editableStocks.length} Item
                        {editableStocks.length !== 1 ? "s" : ""}
                      </span>
                    </h3>
                    <p
                      style={{
                        margin: "8px 0 0 0",
                        fontSize: "0.9rem",
                        color: "#64748b",
                      }}
                    >
                      💙 <strong>Blue:</strong> Warehouse Locations
                      <br />
                      <span style={{ fontSize: "0.8rem", fontStyle: "italic" }}>
                        {bulkUpdateEnabled
                          ? "Click material names to expand/collapse. Edit quantity updates in the 'Update' columns. Use +/- values for adjustments. For 0 quantity stocks, enter positive numbers to add stock. Click 'Add Warehouse Stock' to add new locations."
                          : "Click material names to expand/collapse variants. Enable Quantity Update to edit stock levels."}
                      </span>
                    </p>
                  </div>

                  <div className="stock-table-wrapper">
                    <table
                      key={JSON.stringify(
                        editableStocks.map((g) =>
                          g.combinations.flatMap((c) =>
                            c.warehouseStocks.map((s) => s.warehouseId),
                          ),
                        ),
                      )}
                    >
                      <thead>{renderTableHeaders()}</thead>
                      <tbody>{renderTableRows()}</tbody>
                    </table>
                  </div>

                  <div
                    style={{
                      padding: "16px 24px",
                      borderTop: "1px solid #e2e8f0",
                      background: "#f8fafc",
                      fontSize: "0.9rem",
                      color: "#64748b",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span>
                        💡 <strong>Tip:</strong>{" "}
                        {bulkUpdateEnabled
                          ? "Edit quantity updates directly in the table"
                          : "Click 'Enable Quantity Update' to start editing"}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="stock-update__empty-state">
                  <i className="fas fa-box-open stock-update__empty-icon" />
                  <h3>No Stock Data Available</h3>
                  <p>
                    No{" "}
                    {activeTab === "raw-materials"
                      ? "raw materials"
                      : "products"}{" "}
                    stock found.
                  </p>
                </div>
              )}
            </div>
          )}
          {bulkUpdateEnabled && editableStocks.length > 0 && (
            <div className="stock-update__bottom-actions">
              <ButtonWithIcon
                icon="tdesign:save"
                text="Save All Changes"
                onClick={handleBulkUpdate}
                className="submit-button"
              />

              <ButtonWithIcon
                icon="fluent:dismiss-24-regular"
                text="Cancel Changes"
                onClick={async () => {
                  setBulkUpdateEnabled(false);
                  await loadMaterials();
                }}
                className="stock-update-button cancel-button"
              />
            </div>
          )}
        </div>

        <button
          type="button"
          className="stock-update__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside
          className={`stock-update__sidebar ${
            isSidebarCollapsed ? "stock-update__sidebar--collapsed" : ""
          }`}
          aria-hidden={isSidebarCollapsed}
        >
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>
            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Enable Quantity Update</strong>
                    <span>Switch to edit mode to adjust stock quantities.</span>
                  </li>
                  <li>
                    <strong>Adjust Quantities</strong>
                    <span>
                      Use +/- values to add or subtract stock per warehouse.
                    </span>
                  </li>
                  <li>
                    <strong>Save Changes</strong>
                    <span>
                      Submit to update stock across all affected warehouses.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Double-check quantities before saving</li>
              <li>Use Table View for a quick read-only overview</li>
              <li>Keep warehouse locations consistent across combinations</li>
              <li>Review zero-stock items regularly to restock in time</li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Saving quantity updates modifies stock data immediately. Negative
              adjustments cannot reduce stock below zero.
            </p>
          </div>
        </aside>
      </div>

      {qrTarget && (
        <StockQRModal target={qrTarget} onClose={() => setQrTarget(null)} />
      )}

      {showScanner && (
        <StockQRScanner
          onScan={handleScanned}
          onClose={() => setShowScanner(false)}
        />
      )}

      {scannedParams && (
        <StockScanPopup
          params={scannedParams}
          onClose={() => setScannedParams(null)}
          onChanged={loadMaterials}
        />
      )}

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => setShowWarningPopup(false)}
        onDecline={() => setShowWarningPopup(false)}
        title="Confirm Stock Update"
        message="Are you sure you want to update stock quantities? This action will modify stock data immediately and cannot be automatically undone."
        acceptText="Yes, Update Stock"
        declineText="Cancel"
      />
    </div>
  );
};

export default StockUpdate;
